<template>
  <div>
    <div
      v-if="previourYearError"
      class="flex items-center px-4 py-2 mt-4 mb-4 text-xs text-red-700 bg-red-100 border border-red-400 rounded-full w-fit"
    >
      <InfoRoundedSmallThin class="mr-2" />
      <span>
        To view Business As Usual (BAU) chart line, please add emissions for the
        {{ previousYear }} (Previous year).
      </span>
    </div>
    <div
      v-show="chartSeries[2] && chartSeries[2].data.length"
      class="w-full"
    >
      <apex-chart
        v-if="chartSeries.length"
        ref="targetSettingChart"
        type="line"
        height="450"
        :options="targetSettingChartOptions"
        :series="chartSeries"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'GhgTargetSettingChart',

  props: {
    scopes: {
      type: Array,
      required: true,
    },
    baseYear: {
      type: [Number, String],
      required: true,
    },
    actionYear: {
      type: [Number, String],
      required: true,
    },
    annualPercentage: {
      type: [Number, String],
      required: true,
    },
    carbonReduction: {
      type: Number,
      required: true,
    },
    currentYearEmission: {
      type: [Number, String],
      required: true,
    },
    previousYearEmission: {
      type: Number,
      required: true,
    },
    previousYear: {
      type: Number,
      required: true,
    },
    appCurrentYear: {
      type: Number,
      required: true,
    },
    selectedBaseYear: {
      type: [Number, String],
      required: true,
    },
    selectedBaseYearEmission: {
      type: [Number, String],
      required: true,
    },
    chartLoading: {
      type: Boolean,
      default: false,
    },
    hiddenNumber: {
      type: Number,
      default: 0,
    },
    graphEmissionData: {
      type: Object,
      required: true,
    },
  },

  emits: ['loadingChanged'],

  data() {
    return {
      selectedScopes: this.scopes,
      actualEmissionData: {},
      chartSeries: [
        {
          name: 'Business As Usual',
          type: 'area',
          data: [],
        },
        {
          name: 'Absolute Contraction Approach (ACA) Target',
          type: 'area',
          data: [],
        },
        {
          name: 'Actual Emission',
          type: 'column',
          data: [],
        },
      ],
      baseYearValue: this.baseYear,
      actionYearValue: this.actionYear,
      annualPercentageValue: this.annualPercentage,
      carbonReductionValue: this.carbonReduction,
      currentYearEmissionValue: this.currentYearEmission,
      previousYearEmissionValue: this.previousYearEmission,
      previousYearValue: this.previousYear,
      selectedBaseYearValue: this.selectedBaseYear,
      selectedBaseYearEmissionValue: this.selectedBaseYearEmission,
      appCurrentYearValue: this.appCurrentYear,
      hiddenNumberValue: this.hiddenNumber,
      targetSettingChartOptions: {
        chart: {
          height: 450,
          type: 'area',
          stacked: false,
        },
        stroke: {
          width: [2, 2, 0],
        },
        plotOptions: {
          bar: {
            columnWidth: '80%',
            strokeWidth: 0,
          },
        },
        fill: {
          opacity: [0.25, 0.8, 1],
          type: ['gradient', 'solid', 'solid'],
          gradient: {
            type: 'vertical',
            opacityFrom: 0.6,
            opacityTo: 0.8,
            shadeIntensity: 0.5,
            colorStops: [
              {
                offset: 0,
                color: '#F54B64',
                opacity: 0.4,
              },
              {
                offset: 100,
                color: '#F78361',
                opacity: 0.5,
              },
            ],
          },
        },
        labels: [],
        colors: ['#F54B64', '#B4DDD9', '#31579B'],
        markers: {
          size: 0,
        },
        xaxis: {
          showAlways: true,
          labels: {
            show: true,
            rotate: -90,
            rotateAlways: true,
            hideOverlappingLabels: true,
          },
        },
        yaxis: {
          title: {
            text: 'Emissions (tCO2e)',
          },
          showAlways: true,
          floating: false,
          forceNiceScale: true,
          tickAmount: 5,
          labels: {
            show: true,
            formatter(val) {
              return thousands(val, 2)
            },
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom: function ({ series, _, dataPointIndex, w }) {
            const seriesNames = w.globals.seriesNames
            const colors = w.globals.colors

            let tooltipContent = '<div class="p-4 text-sm bg-white border border-gray-200 rounded-lg shadow-lg custom-tooltip">'

            series.forEach((value, index) => {
              // Check if the series has data (not filtered out)
              if (value[dataPointIndex] !== null && value[dataPointIndex] !== undefined) {
                tooltipContent += `
                  <div class="flex items-center mt-2">
                    <span class="w-3 h-3 mr-2 rounded-sm" style="background-color: ${colors[index]}"></span>
                    <span class="flex-1 mr-2">${seriesNames[index]}:</span>
                    <span class="font-semibold no-localize-translate">${thousands(value[dataPointIndex], 2)} tCO2e</span>
                  </div>
                `
              }
            })

            tooltipContent += '</div>'
            return tooltipContent
          },
          x: {
            show: false,
          },
          y: {
            formatter(y) {
              if (typeof y !== 'undefined') {
                return thousands(y, 2) + ' tCO2e'
              }
              return y
            },
          },
        },
      },
      ACATargetCalculation: localStorage.getItem('ACATargetCalculation') ? JSON.parse(localStorage.getItem('ACATargetCalculation')) : [],
      BAUCalculation: localStorage.getItem('BAUCalculation') ? JSON.parse(localStorage.getItem('BAUCalculation')) : [],
      years: [],
      chartSeriesUnfiltered: [],
      previourYearError: false,
      chartUpdateTimer: null,
      lastUpdateParams: null,
    }
  },

  computed: {
    yearsHaveErrors() {
      return this.actionYearValue !== '' && this.baseYearValue !== 'all' ? parseInt(this.baseYearValue) > parseInt(this.actionYearValue) : false
    },
  },

  watch: {
    scopes(newScopes) {
      const chartSeriesData = this.updatedSeries(newScopes)
      nextTick(() => {
        const unfilteredEmissionsLength = chartSeriesData[2].data.length
        const difference = unfilteredEmissionsLength - this.BAUCalculation.length
        chartSeriesData[2].data = [...chartSeriesData[2].data].filter((_, index) => index >= difference)
        if (this.$refs.targetSettingChart) {
          this.$refs.targetSettingChart.updateSeries(chartSeriesData, true, true)
        }
      })
    },
    baseYear(newBaseYear) {
      this.baseYearValue = newBaseYear
      this.targetSettingChartOptions.labels = [...this.years].map(year => parseInt(year)).filter(year => year >= parseInt(this.baseYearValue)).map(year => year.toString())

      // add null checks to prevent errors when chart data isn't loaded yet
      if (this.chartSeriesUnfiltered && this.chartSeriesUnfiltered[2] && this.chartSeriesUnfiltered[2].data) {
        const filteredYearsLength = this.targetSettingChartOptions.labels.length
        const unfilteredEmissionsLength = this.chartSeriesUnfiltered[2].data.length
        const difference = unfilteredEmissionsLength - filteredYearsLength
        this.chartSeries[2].data = this.updatedSeries(this.scopes)[2].data.filter((_, index) => index >= difference)
        if (this.$refs.targetSettingChart) {
          this.$refs.targetSettingChart.updateSeries(this.chartSeries, true, true)
        }
      }

      if (this.$refs.targetSettingChart) {
        this.$refs.targetSettingChart.updateOptions(this.targetSettingChartOptions, true, true)
      }
      this.debouncedUpdateCharts()
    },
    actionYear(newActionYear) {
      this.actionYearValue = newActionYear
      this.debouncedUpdateCharts()
    },
    annualPercentage(newAnnualPercentage) {
      this.annualPercentageValue = newAnnualPercentage
      this.debouncedUpdateCharts()
    },
    carbonReduction(newCarbonReduction) {
      this.carbonReductionValue = newCarbonReduction
      this.debouncedUpdateCharts()
    },
    currentYearEmission(newCurrentYearEmission) {
      this.currentYearEmissionValue = newCurrentYearEmission
      this.debouncedUpdateCharts()
    },
    previousYearEmission(newPreviousYearEmission) {
      this.previousYearEmissionValue = newPreviousYearEmission
      this.debouncedUpdateCharts()
    },
    previousYear(newPreviousYear) {
      this.previousYearValue = newPreviousYear
    },
    selectedBaseYearEmission(newSelectedBaseYearEmission) {
      this.selectedBaseYearEmissionValue = newSelectedBaseYearEmission
      this.debouncedUpdateCharts()
    },
    ACATargetCalculation(newACATargetCalculation) {
      this.chartSeries[1].data = newACATargetCalculation
      if (this.$refs.targetSettingChart) {
        this.$refs.targetSettingChart.updateSeries(this.chartSeries, true)
      }
    },
    BAUCalculation(newBAUCalculation) {
      this.chartSeries[0].data = newBAUCalculation
      if (this.$refs.targetSettingChart) {
        this.$refs.targetSettingChart.updateSeries(this.chartSeries, true)
      }
    },
    appCurrentYear(newAppCurrentYear) {
      this.appCurrentYearValue = newAppCurrentYear
      this.debouncedUpdateCharts()
    },
    selectedBaseYear(newSelectedBaseYear) {
      this.selectedBaseYearValue = newSelectedBaseYear
      this.debouncedUpdateCharts()
    },
    hiddenNumber(newHiddenNumber) {
      this.hiddenNumberValue = newHiddenNumber
      this.debouncedUpdateCharts()
    },
    graphEmissionData(newData) {
      this.fetchGhgData(newData)
    },
  },

  mounted() {
    // ensure chart is properly initialized when component mounts
    this.$nextTick(() => {
      this.ensureChartReady()
    })
  },

  updated() {
    // ensure chart is available after DOM updates
    this.$nextTick(() => {
      this.ensureChartReady()
    })
  },

  beforeUnmount() {
    // Clear any pending chart update timers
    if (this.chartUpdateTimer) {
      clearTimeout(this.chartUpdateTimer)
    }
  },

  methods: {
    // Ensure chart is ready and properly initialized
    ensureChartReady() {
      if (this.$refs.targetSettingChart && this.graphEmissionData && Object.keys(this.graphEmissionData).length > 0) {
        // chart is available and we have data, trigger updates if needed
        if (!this.chartSeries[2] || !this.chartSeries[2].data.length) {
          // chart data is empty, fetch and populate
          this.fetchGhgData(this.graphEmissionData)
        }
        else {
          this.debouncedUpdateCharts()
        }
      }
    },

    // helper method to safely get base year emission from chart data
    getBaseYearEmissionFromChart(baseYearIndex) {
      // check if all required data structures exist
      if (
        baseYearIndex === -1 || !this.chartSeriesUnfiltered
      ) {
        return null
      }

      // use optional chaining to safely access nested properties
      const emission = this.chartSeriesUnfiltered[2]?.data?.[baseYearIndex]
      // return emission if it exists, otherwise return null
      return emission !== undefined && emission !== null ? emission : null
    },

    // Debounced version of chart updates to prevent UI freezing
    debouncedUpdateCharts() {
      if (this.chartUpdateTimer) {
        clearTimeout(this.chartUpdateTimer)
      }

      this.chartUpdateTimer = setTimeout(() => {
        this.updateCharts()
      }, 150)
    },

    // Batch both chart updates together with async processing
    async updateCharts() {
      const currentParams = {
        annualPercentage: this.annualPercentageValue,
        carbonReduction: this.carbonReductionValue,
        baseYear: this.baseYearValue,
        actionYear: this.actionYearValue,
        baseYearEmission: this.selectedBaseYearEmissionValue,
        previousYearEmission: this.previousYearEmissionValue,
        currentYear: this.appCurrentYearValue,
      }

      // Skip update if parameters haven't changed
      if (this.lastUpdateParams && JSON.stringify(currentParams) === JSON.stringify(this.lastUpdateParams)) {
        return
      }

      this.lastUpdateParams = currentParams

      // Use requestAnimationFrame for smoother updates
      await this.$nextTick()

      // Update both calculations
      this.updateACATargetChart()
      this.updateBAUChart()
    },

    updateACATargetChart() {
      if (this.yearsHaveErrors) {
        this.ACATargetCalculation = []
      }
      else {
        this.$emit('loadingChanged', true)

        // get base year emission from selected value or fallback to chart data
        let baseYearEmission = this.selectedBaseYearEmissionValue

        if (!baseYearEmission || baseYearEmission === 0) {
          const baseYearIndex = this.years.findIndex(year => parseInt(year) === parseInt(this.selectedBaseYearValue))
          const chartEmission = this.getBaseYearEmissionFromChart(baseYearIndex)

          if (chartEmission !== null) {
            baseYearEmission = chartEmission
          }
        }

        if (!baseYearEmission || baseYearEmission === 0 || isNaN(baseYearEmission)) {
          this.ACATargetCalculation = []
          this.$emit('loadingChanged', false)
          return
        }

        const carbonReduction = parseFloat(this.carbonReductionValue)

        // validate carbonReduction value
        if (isNaN(carbonReduction)) {
          this.ACATargetCalculation = []
          this.$emit('loadingChanged', false)
          return
        }

        const differenceYears = 2050 - Number(this.selectedBaseYearValue)
        const ACATargetEmissions = []

        for (let i = 0; i <= differenceYears; i++) {
          // use compound  annual reduction formula instead of linear
          // each year, emissions are reduced by the carbonReduction percentage
          const ACATargetEmission = baseYearEmission * Math.pow(1 - carbonReduction, i)

          // ensure the result is valid and not NaN
          if (isNaN(ACATargetEmission)) {
            ACATargetEmissions.push(0)
          }
          else {
            ACATargetEmissions.push(Math.max(0, ACATargetEmission).toFixed(1))
          }
        }

        this.ACATargetCalculation = ACATargetEmissions
        localStorage.setItem('ACATargetCalculation', JSON.stringify(ACATargetEmissions))

        this.$emit('loadingChanged', false)
      }
    },
    updateBAUChart() {
      if (this.yearsHaveErrors) {
        this.BAUCalculation = []
      }
      else {
        this.$emit('loadingChanged', true)

        const previousYearEmissionsData = this.previousYearEmissionValue

        // if previous year emission is 0, then show error message
        if (previousYearEmissionsData === 0 || previousYearEmissionsData === null || previousYearEmissionsData === undefined) {
          this.previourYearError = true
        }
        else {
          this.previourYearError = false
        }

        // validate previous year emissions data
        if (isNaN(previousYearEmissionsData) || previousYearEmissionsData <= 0) {
          this.BAUCalculation = []
          this.$emit('loadingChanged', false)
          return
        }

        // validate annual percentage value
        if (this.annualPercentageValue === '' || this.annualPercentageValue === null || this.annualPercentageValue === undefined || isNaN(this.annualPercentageValue)) {
          this.BAUCalculation = []
          this.$emit('loadingChanged', false)
          return
        }

        const annualGrowth = parseFloat(this.annualPercentageValue / 100)

        // additional check for invalid calculation result
        if (isNaN(annualGrowth)) {
          this.BAUCalculation = []
          this.$emit('loadingChanged', false)
          return
        }
        const currentYear = this.appCurrentYearValue
        const BAUEmissions = []
        for (let startYear = this.selectedBaseYearValue; startYear < currentYear; startYear++) {
          BAUEmissions.push(previousYearEmissionsData.toFixed(2))
        }

        for (let thisYear = currentYear; thisYear <= 2050; thisYear++) {
          const yearsSinceBaseYear = thisYear - currentYear
          const BAUEmission = previousYearEmissionsData * Math.pow(1 + annualGrowth, yearsSinceBaseYear + 1)

          // ensure the result is valid and not NaN
          if (isNaN(BAUEmission)) {
            BAUEmissions.push(0)
          }
          else {
            BAUEmissions.push(BAUEmission.toFixed(2))
          }
        }
        this.BAUCalculation = BAUEmissions
        localStorage.setItem('BAUCalculation', JSON.stringify(BAUEmissions))

        this.$emit('loadingChanged', false)
      }
    },
    updatedSeries(newScopes) {
      this.selectedScopes = newScopes
      const chartData = this.chartSeries
      const { scope_one, scope_two, scope_three } = this.actualEmissionData
      let actualEmissionSeriesData = []

      for (const [index, scope] of [scope_one, scope_two, scope_three].entries()) {
        if (index !== 3 && !newScopes.includes(index + 1)) continue

        const keys = Object.keys(scope)

        if (!index) {
          actualEmissionSeriesData = scope[keys[1]]
        }
        if (index) {
          actualEmissionSeriesData = !actualEmissionSeriesData.length
            ? scope[keys[1]]
            : actualEmissionSeriesData.map((value, dataIndex) => {
              if (index === 3) {
                return !value ? scope[keys[1]][dataIndex] : value
              }
              return value + scope[keys[1]][dataIndex]
            })
        }
      }
      chartData[2].data = newScopes.length
        ? actualEmissionSeriesData.map((value) => {
          return value ? Number(value.toFixed(2)) : 0.0
        })
        : []
      return chartData
    },
    fetchGhgData(newGraphData) {
      this.actualEmissionData = newGraphData.data
      const { scope_one, scope_two, scope_three, total_emissions }
        = this.actualEmissionData;
      [scope_one, scope_two, scope_three, total_emissions].forEach((scope, index) => {
        const keys = Object.keys(scope)
        if (index === 0) {
          this.years = scope.year.map(year => year.toString())
          this.targetSettingChartOptions.labels = scope.year.filter(year => year >= parseInt(this.baseYearValue)).map(year => year.toString())
          this.chartSeries[2].data = scope[keys[1]].map((value) => {
            return value ? Number(value.toFixed(2)) : 0.0
          })
        }
        else {
          this.chartSeries[2].data = this.chartSeries[2].data.map(
            (value, dataIndex) => {
              if (index === 3) {
                return !value
                  ? Number(scope[keys[1]][dataIndex].toFixed(2))
                  : Number(value.toFixed(2))
              }
              return value
                ? Number(value.toFixed(2))
                + Number(scope[keys[1]][dataIndex].toFixed(2))
                : Number(scope[keys[1]][dataIndex].toFixed(2))
            },
          )
        }
      },
      )

      this.chartSeriesUnfiltered = this.chartSeries
      const filteredYearsLength = this.targetSettingChartOptions.labels.length
      const unfilteredEmissionsLength = this.chartSeries[2].data.length
      const difference = unfilteredEmissionsLength - filteredYearsLength
      this.chartSeries = [...this.chartSeries].map((data, index) => {
        if (index === 2) {
          return {
            ...data,
            data: data.data.filter((_, index) => index >= difference),
          }
        }
        return data
      })
      if (this.$refs.targetSettingChart) {
        this.$refs.targetSettingChart.updateOptions(
          this.targetSettingChartOptions,
          true,
          true,
        )
      }

      // recalculate charts after data is loaded
      this.debouncedUpdateCharts()
    },
  },
}
</script>
